import { Box, Card, Grid, Paper, Stack, Typography } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMFAttachedDocumentsContext } from '~/microfrontend/hooks/useMFModuleNameContext';
import { ImagesWidgetSection } from '~/widgets/components/ImagesWidgetSection';
import EmptyImagesIcon from '~assets/images/empty/emptyImages.svg?react';
import { EmptyContent } from '~components/reuseable/EmptyContent';
import { FileTypesStore } from '~modules/fileTypes/FileTypesStore';
import { DocumentFixedTypeModel } from '~modules/fileTypes/models/DocumentFixedTypeModel';

export const AttachedImagesWidget = observer(() => {
  const fileTypesStore = useLocalObservable(() => new FileTypesStore());

  const { documentId, documentTypeId, mode, readonly, includeNonGroupType, withoutFixedTypes, title } =
    useMFAttachedDocumentsContext();

  const hasFiles = useMemo(() => {
    return fileTypesStore.documentFixedTypes.some((fixedType) => fixedType.hasFiles);
  }, [fileTypesStore.documentFixedTypes]);

  const fullFileTypesIsEnabled = useMemo(() => {
    return fileTypesStore.documentFixedTypes.some((fixedType) => fixedType.isEnabled);
  }, [fileTypesStore.documentFixedTypes]);

  const { t } = useTranslation('common');

  // Effects

  useEffect(() => {
    if (!readonly) {
      fileTypesStore.getFileFormats(mode);
    }

    if (!withoutFixedTypes) {
      fileTypesStore.getDocumentFixedTypes(documentTypeId, documentId, mode, includeNonGroupType);
    }

    return () => {
      fileTypesStore.reset();
    };
  }, []);

  // Handlers

  const handleRefreshFixedTypesList = () => {
    if (withoutFixedTypes) {
      return;
    }

    fileTypesStore.getDocumentFixedTypes(documentTypeId, documentId, mode, includeNonGroupType);
  };

  // Render

  const renderEmptyContent = () => (
    <Grid item xs={12}>
      <Card elevation={0}>
        <Stack direction="column" gap={1} justifyContent="space-between" sx={{ height: '100%' }}>
          <Box pl={2} pt={2}>
            {title}
          </Box>
          <Box p={2} pt={0} sx={{ flexGrow: 1 }}>
            <EmptyContent IconComponent={<EmptyImagesIcon />} title={t('empty.images.title')} />
          </Box>
        </Stack>
      </Card>
    </Grid>
  );

  const renderImagesWidgetSection = (documentFixedType?: DocumentFixedTypeModel) => {
    return (
      <Grid key={documentFixedType?.fixedType?.id} item xs={12}>
        <Paper elevation={0}>
          <Box p={2}>
            <ImagesWidgetSection
              documentFixedType={documentFixedType}
              fileFormats={fileTypesStore.fileFormats}
              onRefreshFixedTypesList={handleRefreshFixedTypesList}
            />
            {documentFixedType?.isRequired && documentFixedType?.isEnabled && (
              <Typography variant="caption" color="error.main">
                {t('requiredImage')}
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderFixedType = (documentFixedType?: DocumentFixedTypeModel) => {
    const currentReadonlyStatus = readonly || documentFixedType?.isReadOnly;

    if (!documentFixedType?.hasFiles && currentReadonlyStatus) {
      return;
    }

    return renderImagesWidgetSection(documentFixedType);
  };

  const renderImagesWidget = () => {
    if (fileTypesStore.documentFixedTypesLoaded && !hasFiles && readonly) {
      return renderEmptyContent();
    }

    if (withoutFixedTypes) {
      return renderImagesWidgetSection();
    }

    return fileTypesStore.documentFixedTypes.map(renderFixedType);
  };

  if (!hasFiles && !fullFileTypesIsEnabled) {
    return renderEmptyContent();
  }

  return (
    <Grid container spacing={1}>
      {renderImagesWidget()}
    </Grid>
  );
});
